// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Loading from 'react-loading';
import { toast } from 'react-toastify';

import Modal from './Modal';

const placeholders = {
    "email": "Enter email address...",
    "password": "Enter your password...",
    "host": "Enter server host...",
    "port": "Enter server port..."
};


const providers = [
    {
        provider: "Gmail",
        title: "Google Mail",
        icon: "gmail.png",
        fields:[],
        auth: "google", 
        help: "Stax·ai has been submitted to the Google Verified Apps Marketplace and is currently pending review. In the meantime, you can follow one of the methods below to setup your email.",
        image: 'https://static.stax.ai/images/icons/google.png'
    },
    {
        provider: "Office365",
        title: "MS Outlook",
        auth: "microsoft",
        icon: "outlook.png",
        fields: [],
        help: "Press the button below to give Stax·ai Permissions to your Microsoft Outlook account, and allow for syncing to your Stax·ai account",
        image: 'https://static.stax.ai/images/icons/add.png'
    },
    {
        provider: "Exchange",
        title: "MS Exchange",
        icon: "exchange.png",
        fields: ["host", "email", "password"],
        help: "Enter your Microsoft Exchange email address and password and Stax·ai will connect to your account. Leave the Host empty to attempt to auto-discover.",
        image: 'https://static.stax.ai/images/icons/add.png'
    },
    {
        provider: "Yahoo",
        title: "Yahoo! Mail",
        icon: "yahoo.png",
        fields: ["email", "password"],
        help: <>Yahoo! Mail requires you to generate an app password to let third-party apps sign in to your account. Follow <a href="https://staxai.zendesk.com/hc/en-us/articles/4404808449300" target="_blank" rel="noopener noreferrer">this guide</a> to generate a Yahoo! app password for Stax·ai.</>,
        image: 'https://static.stax.ai/images/icons/add.png'
    },
    {
        provider: "IMAP",
        title: "IMAP Server",
        icon: "host.png",
        fields: [ "email", "password", "host", "port" ],
        help: "Enter your server configuration below. This is the information you would use to setup Email on your phone or an email client like Outlook.",
        image: 'https://static.stax.ai/images/icons/add.png'
    },
]

export default class AddEmailAccount extends Modal {
    state = {
        config: {
            provider: "",
            email: "",
            password: "",
            host: "",
            port: 993
        },
        alert: {
            email: false,
            password: false,
            host: false,
            port: false
        },
        verifying: false
    }

    renderContent = () => {
        const provider = providers.find(p => p.provider === this.state.config.provider);
        return (
            <div className="modal-content modal-center">

                <h1>Add a new email account</h1>

                <p>Select an email provider and fill out the form to connect to your email account</p>

                <div className="settings-email-provider-list">
                    {
                        providers.map((p, idx) => {
                            return (
                                <div key={idx} className={"settings-email-provider" + (this.state.config.provider === p.provider ? " settings-email-provider-active" : "")}
                                onClick={() => {
                                    this.setState({
                                        config: {
                                            provider: p.provider,
                                            email: "",
                                            password: "",
                                            host: "",
                                            port: 993
                                        },
                                        alert: {
                                            email: false,
                                            password: false,
                                            host: false,
                                            port: false
                                        }

                                    })
                                }}>
                                    <img alt={p.title} src={`https://static.stax.ai/images/mail/${p.icon}`}/>
                                    <h2>{p.title}</h2>
                                </div>
                            )
                        })
                    }
                </div>
                
                {this.state.config.provider && this.state.config.provider !== "" && 
                <div style={{clear: 'both', marginTop: -20}}>
                    
                    <h2>{provider.title} Configuration</h2>
                    <p>{provider.help}</p>
                
                    {provider.fields.map((field, idx) => {
                        return (
                            <div key={idx} className="settings-email-provider-field">
                                <label>{`${field[0].toUpperCase()}${field.substring(1)}`}</label>
                                <div className="settings-email-provider-input">
                                    <input
                                        type={field==="password"? "password": "text"}
                                        id={field}
                                        name={field}
                                        className={this.state.alert[field] ? "input-error": ""}
                                        value={this.state.config[field]}
                                        placeholder={placeholders[field]}
                                        onChange={(e) => {
                                            this.setState({
                                                config: {
                                                    ...this.state.config, 
                                                    [field]: e.target.value
                                                }
                                            })
                                        }}
                                    />
                                </div> 
                            </div> 
                        )
                    })}

                    {/* Are they connecting a gmail account? -> Needs oAuth*/}
                    {provider.provider === "Gmail" && 
                        <div className="settings-email-provider-list settings-email-gmail-list">
                            <div
                                className={"settings-email-provider"}
                                onClick={() => window.open('https://staxai.zendesk.com/hc/en-us/articles/360048669012-How-To-Configure-your-personal-GMail-account-for-Stax-ai-', '_blank')}
                            ><img
                                alt=""
                                src="https://static.stax.ai/images/mail/gmail-personal.png"
                            /><h2>Personal GMail</h2></div>

                            <div
                                className={"settings-email-provider"}
                                onClick={() => window.open('https://staxai.zendesk.com/hc/en-us/articles/360048671472-How-To-Configure-your-enterprise-GSuite-account-for-Stax-ai-', '_blank')}
                            ><img
                                alt=""
                                src="https://static.stax.ai/images/mail/gsuite-corporate.png"
                            /><h2>Enterprise GSuite</h2></div>

                            <div
                                className={"settings-email-provider"}
                                onClick={this.props.helpRequest}
                            ><img
                                alt=""
                                src="https://static.stax.ai/images/mail/google-support.png"
                            /><h2>Help me setup</h2></div>

                            <div style={{ height: 10, clear: 'both' }} />
                            <p>
                                Once you configure your GMail/GSuite account, click on the button below to connect your Google account.
                            </p>
                        </div>
                    }

                    {/* Are they connecting a office365 account? -> Needs oAuth*/}


                    <div style={{ height: 10, clear: 'both' }} />   
                    
                    {this.state.verifying ? 
                        <div style={{width: 80, margin: "0 auto", marginTop: -10}}>
                            <Loading 
                                type="bubbles"
                                color="var(--color-primary-dark)"
                                height={80}
                                width={80}
                            />                            
                        </div>:
                        <div 
                            className="button"
                            style={{float: "none", backgroundImage: `url(${provider.image})`}}
                            onClick={async() => {

                                let alert = {
                                    email: false, 
                                    password: false, 
                                    host: false, 
                                    port: false, 
                                };

                                let ok = true;

                                for(let field of provider.fields){
                                    if (this.state.config[field] === ""){
                                        alert[field] = true;
                                        ok=false;
                                    }
                                }
                                
                                setTimeout(async() => {
                                    this.setState({ alert, verifying: ok });

                                    if(!ok) return;

                                    // Field indicates oAuth is needed
                                    if(provider.auth){
                                        if(!this.props.oAuth)
                                            return toast.error("Oops, something went wrong! We couldn't connect to your email service.")
                                        
                                        let code = await this.props.oAuth(provider.auth);

                                        if (code.error) {
                                            this.setState({ verifying: false })
                                            toast.error(code.error)
                                            return;
                                        }
                                        
                                        this.setState(prevState => (
                                            {
                                                config: {
                                                    ...prevState.config,
                                                    password: code.code? code.code: code
                                                }
                                            }
                                        ), 
                                            async() => {
                                                await this.props.addEmailAccount(this.state.config)
                                            }
                                        )
                                    }else {
                                        let config = { ...this.state.config };
                                        if (config.provider === "Yahoo") {
                                            config.provider = "IMAP";
                                            config.host = "imap.mail.yahoo.com";
                                        }
                                        await this.props.addEmailAccount(config);
                                    }

                                    setTimeout(() => this.setState({ verifying: false }), 150);
                                }, 50)

                            }}
                        >{provider.auth? `Connect ${provider.title} Account`: "Verify & Add Account"}</div>
                    }

                </div>
                }

            </div>
        )
    }
}
